"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { AuctionButton } from "@/components/ui/auctionButton";
import {
  Clock,
  Users,
  Facebook,
  Instagram,
  Twitter,
  CalendarPlus,
  Calendar,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Share2,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

export type AuctionItem = {
  id: string;
  title: string;
  category: string;
  image: string;
  auctiontype: "forward" | "reverse";
  status: "live" | "upcoming" | "closed";
  location: string;
  productname: string;
  fname: string;
  role: string;
  featured?: boolean;
  startprice: number;
  verified?: boolean;
  currentBid?: number;
  timeLeft?: string;
  bidders?: number;
  seller?: string;
  rating?: number;
  targetPrice?: number;
  bidincrementtype?: "fixed" | "percentage";
  minimumincrement?: number;
  deadline?: string;
  proposals?: number;
  buyer?: string;
  currency?: string;
  startingBid?: number;
  buy_now_price?: number;
  startsIn?: string;
  finalBid?: number;
  ended?: boolean;
  endedAgo?: string;
  winner?: string;
  views?: number;
  percent?: number;
  bidder_count?: number;
  watchers?: number;
  productimages?: string[];
  productdocuments?: string[];
  createdat?: string;
  auctionsubtype?: string;
  scheduledstart?: string;
  auctionduration?: { days?: number; hours?: number; minutes?: number };
  product_heromsg?: string;
  sub_handle?: string;
};

function LiveTimer({ time }: { time: string }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    function update() {
      const end = new Date(time);
      const now = new Date();
      const diff = Math.max(0, end.getTime() - now.getTime());
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${hours > 0 ? hours + "h " : ""}${minutes}m ${seconds}s`);
    }
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [time]);

  return (
    <span className="inline-flex min-w-[6.5rem] items-center gap-1 whitespace-nowrap font-normal tabular-nums text-sm text-red-600">
      <Clock className="h-3 w-3" />
      {timeLeft}
    </span>
  );
}

export const AuctionCard = ({
  auction,
  onCategoryClick,
  imagePriority = false,
}: {
  auction: AuctionItem;
  onCategoryClick?: (category: string) => void;
  imagePriority?: boolean;
}) => {
  const { user } = useAuth();
  const router = useRouter();
  const isLoggedIn = !!user;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const currencySymbols: { [key: string]: string } = {
    USD: "$",
    INR: "₹",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    CAD: "CA$",
    AUD: "A$",
  };
  const currencySymbol = auction.currency
    ? (currencySymbols[auction.currency] ?? "")
    : "";
  const forwardCurrentBid =
    auction.currentBid !== undefined && (auction.bidder_count || 0) > 0
      ? auction.currentBid
      : auction.startingBid;
  const forwardTimerLabel = auction.status === "upcoming" ? "Starts in" : "Ends in";
  const forwardButtonLabel =
    user?.role === "seller" && isLoggedIn
      ? auction.status === "live"
        ? "View"
        : auction.status === "closed"
          ? "Auction Summary"
          : "View Auction"
      : user?.role === "buyer" && isLoggedIn
        ? auction.status === "live"
          ? "View & Bid"
          : auction.status === "closed"
            ? "Auction Summary"
            : "View Auction"
        : "View Auction";
  const reverseCurrentBid =
    auction.currentBid !== undefined && (auction.bidder_count || 0) > 0
      ? auction.currentBid
      : auction.startingBid;
  const reverseButtonLabel =
    user?.role === "seller" && isLoggedIn
      ? "View & Bid"
      : user?.role === "buyer" && isLoggedIn
        ? auction.status === "closed"
          ? "Auction Summary"
          : "View"
        : "View Auction";
  const isForwardAuction = auction.auctiontype === "forward";
  const CardAccentIcon = isForwardAuction ? TrendingUp : TrendingDown;
  const cardAccentLabel = isForwardAuction ? "Selling" : "Buying";
  const cardAccentIconClass = isForwardAuction
    ? "text-emerald-500"
    : "text-blue-500";
  const cardCurrentBid =
    auction.status === "upcoming"
      ? auction.startingBid
      : isForwardAuction
        ? forwardCurrentBid
        : reverseCurrentBid;
  const cardBidLabel =
    auction.status === "upcoming"
      ? "Starting Bid"
      : auction.status === "closed"
        ? "Final Bid"
        : "Current Bid";
  const cardButtonLabel = isForwardAuction
    ? forwardButtonLabel
    : reverseButtonLabel;
  const cardTimerLabel = forwardTimerLabel;

  const imagesOnly = useMemo(() => {
    return (auction.productimages || []).filter((url) => {
      const ext = url.split(".").pop()?.toLowerCase();
      return !["mp4", "webm", "ogg", "mov"].includes(ext || "");
    });
  }, [auction.productimages]);

  useEffect(() => {
    if (imagesOnly && imagesOnly.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) =>
          prev === imagesOnly.length - 1 ? 0 : prev + 1,
        );
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [imagesOnly]);

  const currentImage = useMemo(() => {
    return imagesOnly.length
      ? imagesOnly[currentImageIndex]
      : auction.image || "/placeholder.svg";
  }, [imagesOnly, currentImageIndex, auction.image]);
  const optimizedImage = currentImage;

  const auctionPath =
    auction.auctiontype === "reverse"
      ? `/auctions/reverse/${auction.id}`
      : `/auctions/${auction.id}`;

  if (auction.auctiontype === "forward" || auction.auctiontype === "reverse") {
    return (
      <Card className="group relative flex h-full flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] transition-all duration-300 hover:shadow-[0_24px_70px_rgba(15,23,42,0.14)] dark:border-[#2D3748] dark:bg-[#1C252E]">
        <div className="relative aspect-[4/3] overflow-hidden">
          <Link href={auctionPath} className="block h-full w-full">
            <Image
              src={optimizedImage}
              alt={auction.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 92vw, (max-width: 1024px) 46vw, (max-width: 1280px) 31vw, 345px"
              quality={60}
              priority={imagePriority}
              loading={imagePriority ? "eager" : "lazy"}
              fetchPriority={imagePriority ? "high" : "auto"}
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </Link>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/50 to-transparent" />

          <div className="pointer-events-none absolute left-4 top-4">
            <span className="inline-flex rounded-lg bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
              {auction.auctionsubtype
                ? auction.auctionsubtype.charAt(0).toUpperCase() +
                  auction.auctionsubtype.slice(1)
                : "Auction"}
            </span>
          </div>

          <div className="pointer-events-none absolute right-4 top-4">
            {auction.status === "live" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-semibold text-white shadow-lg shadow-emerald-500/25">
                <span className="h-2 w-2 rounded-full bg-white" />
                LIVE
              </span>
            ) : auction.status === "upcoming" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3 py-1 text-[12px] font-semibold text-white shadow-lg shadow-blue-500/25">
                <Calendar className="h-3 w-3" />
                UPCOMING
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-700 px-3 py-1 text-[12px] font-semibold text-white">
                <CheckCircle className="h-3 w-3" />
                CLOSED
              </span>
            )}
          </div>

          <div className="pointer-events-none absolute inset-x-4 bottom-4 flex items-end justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/70 px-[10px] py-[3px] text-[11px] font-semibold text-slate-800 shadow-lg backdrop-blur-md">
              <CardAccentIcon className={`h-3.5 w-3.5 ${cardAccentIconClass}`} />
              {cardAccentLabel}
            </span>

            <div className="pointer-events-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="h-7 w-7 rounded-lg border-0 bg-white/70 p-0 text-slate-700 shadow-lg backdrop-blur-md hover:bg-slate-50"
                aria-label="Add auction to calendar"
                onClick={() => {
                  window.open(
                    `https://www.google.com/calendar/render?action=TEMPLATE&text=Auction+Event&dates=20250711T180000Z/20250711T190000Z&details=Join+the+auction+at+https://yourdomain.com&location=https://yourdomain.com`,
                    "_blank",
                  );
                }}
              >
                <CalendarPlus className="h-4 w-4 text-emerald-500" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-7 w-7 rounded-lg border-0 bg-white/70 p-0 text-slate-700 shadow-lg backdrop-blur-md hover:bg-slate-50"
                aria-label="Share auction"
                onClick={() => setShowShareMenu(!showShareMenu)}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {showShareMenu && (
            <div className="absolute bottom-20 right-4 z-30 flex gap-3 rounded-2xl border bg-white p-3 shadow-xl">
              <a
                href="https://www.facebook.com/sharer/sharer.php?u=https://yourdomain.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="h-5 w-5 text-blue-600 transition hover:scale-110" />
              </a>
              <a
                href="https://www.instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="h-5 w-5 text-pink-500 transition hover:scale-110" />
              </a>
              <a
                href="https://twitter.com/intent/tweet?url=https://yourdomain.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter className="h-5 w-5 text-blue-400 transition hover:scale-110" />
              </a>
            </div>
          )}
        </div>

        <CardContent className="flex flex-1 flex-col gap-3 p-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="rounded-full border-slate-200 bg-slate-50 px-[10px] py-[3px] text-[11px] font-medium text-slate-700 hover:bg-slate-100"
                onClick={() => onCategoryClick?.(auction.category)}
              >
                {auction.category}
              </Badge>
            </div>
            <Link href={auctionPath} className="mt-1 block">
              <h3 className="line-clamp-2 min-h-[3.5rem] text-[18px] font-normal leading-snug text-[#111111] dark:text-white">
                {auction.title}
              </h3>
            </Link>
            {(auction.status === "live" || auction.status === "upcoming") &&
            (auction.timeLeft || auction.startsIn) ? (
              <div className="flex items-center gap-2 text-base">
                <span className="text-sm font-medium text-slate-500 dark:text-slate-300">
                  {cardTimerLabel}
                </span>
                <span className="font-normal text-red-500">
                  <LiveTimer
                    time={
                      auction.status === "upcoming"
                        ? auction.startsIn!
                        : auction.timeLeft!
                    }
                  />
                </span>
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-100 pt-1 dark:border-[#2D3748]">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {cardBidLabel}
            </p>
            <div className="mt-2 flex items-end justify-between gap-4">
              <p className="text-[22px] font-normal tracking-tight text-[#111111] dark:text-white">
                {auction.auctionsubtype === "sealed"
                  ? "Confidential"
                  : cardCurrentBid !== undefined
                    ? `${currencySymbol}${cardCurrentBid.toLocaleString()}`
                    : "No bids placed"}
              </p>
              <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-200">
                <Users className="h-3 w-3" />
                <span className="text-sm font-semibold">
                  {auction.bidder_count ?? auction.bidders ?? 0}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-1">
            <AuctionButton
              className="text-[13px]"
              onClick={() => router.push(auctionPath)}
            >
              {cardButtonLabel}
            </AuctionButton>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
